<template v-if="employer">
  <div class="employer" v-bind="$attrs">
    <label v-if="isCurrentEmployer">Current Employer</label>
    <div class="sc-form d-flex align-items-center">
      <input
        ref="fieldRef"
        :name="name"
        :id="name"
        type="text"
        v-model="value"
        @change="handleInput"
      />
      <span :class="isCurrentEmployer ? 'icon-pencil' : 'icon-x'" @click="handleIconClick"></span>
    </div>
    <ErrorMessage v-if="errors" class="error" :name="name"></ErrorMessage>
  </div>
</template>

<script setup lang="ts">
import {
  toRef,
  ref,
  computed,
  onMounted,
  defineEmits,
  defineExpose,
  defineProps,
  watch,
} from "vue";
import { ErrorMessage, useField } from "vee-validate";
import { EmployerTypeIds, EmployerRecord } from "@/models/employer";
import { validateStringInput } from "@/use/validation";

const emit = defineEmits<{
  (e: "deleteEmployer", id: number): void;
  (e: "update-modelValue", value?: any): void;
}>();

interface Props {
  initialValue?: string | null;
  employer: EmployerRecord;
  name: string;
}

const props = defineProps<Props>();

// Template Refs
const fieldRef = ref<HTMLInputElement | null>(null);

const name = toRef(props, "name");

const isCurrentEmployer = computed<boolean>(
  () => props?.employer?.employerTypeId === EmployerTypeIds.Current,
);

function handleInput() {
  emit("update-modelValue", value.value);
}

const { handleChange, value, errors, setValue } = useField(name.value || "employer_0", value => {
  if (isCurrentEmployer.value && !validateStringInput(value)) {
    return "Current Employer is required";
  }
  return true;
});

function handleIconClick() {
  console.log("handleClick");
  if (isCurrentEmployer.value && fieldRef.value) {
    fieldRef.value.disabled = false;
  } else {
    deleteEmployer();
  }
}

function deleteEmployer() {
  emit("deleteEmployer", props.employer.id);
}

onMounted(() => {
  if (props.initialValue) {
    setValue(props.initialValue);

    if (isCurrentEmployer.value && fieldRef.value) {
      fieldRef.value.disabled = true;
    }
  }
});

watch(value, (newValue, oldValue) => {
  // sync it with vee-validate, this will trigger validation
  handleChange(newValue);
});

defineExpose({
  fieldRef,
  value,
	setValue,
});
</script>

<style scoped>
a:hover {
  text-decoration: underline;
}
.container {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
}
.icon-x,
.icon-pencil {
  cursor: pointer;
  margin-left: 10px;
}
span.x {
  font-family: "Montserrat";
  font-weight: 700;
}

input[type="text"]:focus {
  border-color: var(--theme-color);
}

input[type="text"] {
  border-radius: 6px;
  border: 1px solid #ced1d9;
  background: #fff;
  height: 34px;

  height: calc(1.5em + 0.75rem + 2px);
  padding: 0.375rem 0.75rem;
}

.sc-forms {
  display: flex;
  flex-direction: column;
  row-gap: 10px;
}

.sc-form input {
  width: calc(100% - 26px);
}

.employer label {
  margin-bottom: 11px;
  color: #000;
  font-family: "Montserrat";
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
}

.error {
  color: #bb0000;
}

.icon-pencil {
  margin-left: 12px;
}
</style>
